import React from 'react';
import { EditorContent, useEditor } from '@tiptap/react';
import { useTranslation } from 'react-i18next';
import { Bold, Code, Italic, Link2, Link2Off, List, ListOrdered, Quote, Strikethrough } from 'lucide-react';
import { Toggle } from '@/components/ui/toggle';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Separator } from '@/components/ui/separator';
import { richTextExtensions } from '@/lib/rich-text-extensions';
import { cn } from '@/lib/utils';
/**
 * WYSIWYG editor for campaign descriptions. Reads and writes **markdown** —
 * the storage format is unchanged, so the Twig `|markdown` filter and
 * `<MarkdownPreview>` keep rendering these values exactly as before. The
 * editing schema, and why it is narrowed the way it is, lives in
 * {@link richTextExtensions}.
 */
export function RichTextEditor({ value, onChange, onBlur, placeholder, disabled = false, id, className, 'aria-describedby': ariaDescribedBy, }) {
    const editor = useEditor({
        editable: !disabled,
        extensions: richTextExtensions(placeholder),
        content: value,
        editorProps: {
            attributes: {
                id: id ?? '',
                class: cn('min-h-[120px] w-full px-3 py-2 text-sm leading-relaxed outline-none', '[&>*]:m-0 [&>*+*]:mt-2', '[&_a]:text-cyan-600 [&_a]:underline dark:[&_a]:text-cyan-400', '[&_ul]:list-disc [&_ul]:pl-5 [&_ol]:list-decimal [&_ol]:pl-5', '[&_blockquote]:border-l-2 [&_blockquote]:border-border [&_blockquote]:pl-3 [&_blockquote]:italic', '[&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:text-xs', 
                // Placeholder: the Placeholder extension tags the empty
                // first paragraph and parks the text in `data-placeholder`;
                // this draws it without a stylesheet.
                '[&_p.is-editor-empty:first-child]:before:float-left', '[&_p.is-editor-empty:first-child]:before:h-0', '[&_p.is-editor-empty:first-child]:before:pointer-events-none', '[&_p.is-editor-empty:first-child]:before:text-muted-foreground', '[&_p.is-editor-empty:first-child]:before:content-[attr(data-placeholder)]'),
                ...(ariaDescribedBy ? { 'aria-describedby': ariaDescribedBy } : {}),
            },
        },
        onUpdate: ({ editor: instance }) => {
            onChange(instance.storage.markdown.getMarkdown());
        },
        onBlur: () => onBlur?.(),
    });
    // Keep the editor in step when the form resets around it (loading a
    // campaign, picking a template). Comparing against the editor's own
    // serialised markdown is what stops the feedback loop: without it every
    // keystroke would round-trip back through `setContent` and park the
    // caret at position 0.
    React.useEffect(() => {
        if (!editor)
            return;
        if (value === editor.storage.markdown.getMarkdown())
            return;
        editor.commands.setContent(value, { emitUpdate: false });
    }, [editor, value]);
    React.useEffect(() => {
        editor?.setEditable(!disabled);
    }, [editor, disabled]);
    if (!editor) {
        return <div className={cn('min-h-[168px] rounded-md border border-input bg-background', className)}/>;
    }
    return (<div className={cn('rounded-md border border-input bg-background focus-within:ring-[3px] focus-within:ring-ring/50', disabled && 'pointer-events-none opacity-50', className)}>
            <RichTextToolbar editor={editor} disabled={disabled}/>
            <EditorContent editor={editor}/>
        </div>);
}
/**
 * Formatting controls. Each toggle reflects the mark under the caret, so the
 * bar doubles as a read-out of where the user is in the document.
 */
function RichTextToolbar({ editor, disabled }) {
    const { t } = useTranslation();
    const [linkOpen, setLinkOpen] = React.useState(false);
    const [linkDraft, setLinkDraft] = React.useState('');
    // Subscribing to the transaction stream is what keeps the toggles in sync
    // with the caret — Tiptap mutates the editor in place, so React has no
    // other reason to re-render this bar.
    const [, forceRender] = React.useReducer((tick) => tick + 1, 0);
    React.useEffect(() => {
        editor.on('transaction', forceRender);
        return () => {
            editor.off('transaction', forceRender);
        };
    }, [editor]);
    const openLinkEditor = React.useCallback(() => {
        setLinkDraft(editor.getAttributes('link').href ?? '');
        setLinkOpen(true);
    }, [editor]);
    const applyLink = React.useCallback(() => {
        const href = linkDraft.trim();
        if (href === '') {
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
        }
        else {
            editor.chain().focus().extendMarkRange('link').setLink({ href }).run();
        }
        setLinkOpen(false);
    }, [editor, linkDraft]);
    return (<div className="flex flex-wrap items-center gap-0.5 border-b border-input px-1.5 py-1">
            <Toggle size="sm" pressed={editor.isActive('bold')} onPressedChange={() => editor.chain().focus().toggleBold().run()} disabled={disabled} aria-label={t('rich_text.bold', { defaultValue: 'Bold' })}>
                <Bold />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive('italic')} onPressedChange={() => editor.chain().focus().toggleItalic().run()} disabled={disabled} aria-label={t('rich_text.italic', { defaultValue: 'Italic' })}>
                <Italic />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive('strike')} onPressedChange={() => editor.chain().focus().toggleStrike().run()} disabled={disabled} aria-label={t('rich_text.strike', { defaultValue: 'Strikethrough' })}>
                <Strikethrough />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive('code')} onPressedChange={() => editor.chain().focus().toggleCode().run()} disabled={disabled} aria-label={t('rich_text.code', { defaultValue: 'Inline code' })}>
                <Code />
            </Toggle>

            <Separator orientation="vertical" className="mx-1 !h-5"/>

            <Popover open={linkOpen} onOpenChange={setLinkOpen}>
                <PopoverTrigger asChild>
                    <Toggle size="sm" pressed={editor.isActive('link')} onPressedChange={openLinkEditor} disabled={disabled} aria-label={t('rich_text.link', { defaultValue: 'Link' })}>
                        <Link2 />
                    </Toggle>
                </PopoverTrigger>
                <PopoverContent align="start" className="w-72 space-y-2">
                    <Input value={linkDraft} onChange={(event) => setLinkDraft(event.target.value)} onKeyDown={(event) => {
            if (event.key === 'Enter') {
                event.preventDefault();
                applyLink();
            }
        }} placeholder="https://example.com" aria-label={t('rich_text.link_url', { defaultValue: 'Link URL' })} autoFocus/>
                    <div className="flex items-center justify-between gap-2">
                        <Button type="button" size="sm" variant="ghost" onClick={() => {
            setLinkDraft('');
            editor.chain().focus().extendMarkRange('link').unsetLink().run();
            setLinkOpen(false);
        }} disabled={!editor.isActive('link')}>
                            <Link2Off className="mr-1 h-3.5 w-3.5"/>
                            {t('rich_text.unlink', { defaultValue: 'Remove' })}
                        </Button>
                        <Button type="button" size="sm" onClick={applyLink}>
                            {t('apply', { defaultValue: 'Apply' })}
                        </Button>
                    </div>
                </PopoverContent>
            </Popover>

            <Separator orientation="vertical" className="mx-1 !h-5"/>

            <Toggle size="sm" pressed={editor.isActive('bulletList')} onPressedChange={() => editor.chain().focus().toggleBulletList().run()} disabled={disabled} aria-label={t('rich_text.bullet_list', { defaultValue: 'Bulleted list' })}>
                <List />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive('orderedList')} onPressedChange={() => editor.chain().focus().toggleOrderedList().run()} disabled={disabled} aria-label={t('rich_text.ordered_list', { defaultValue: 'Numbered list' })}>
                <ListOrdered />
            </Toggle>
            <Toggle size="sm" pressed={editor.isActive('blockquote')} onPressedChange={() => editor.chain().focus().toggleBlockquote().run()} disabled={disabled} aria-label={t('rich_text.quote', { defaultValue: 'Quote' })}>
                <Quote />
            </Toggle>
        </div>);
}
